import { useRouteError, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import * as Sentry from '@sentry/react';
import { Iconify } from '@mfw/components';
import Box from '@mui/material/Box';
import { useIntl } from 'react-intl';
import React from 'react';

import { onSurface } from '../theme/onSurface';
import { useNavigate } from '../router/hooks';
import messages from './messages';

/**
 * The error surface for a crash that happened INSIDE the app shell.
 *
 * `RouteErrorPage` is the other one, and the difference is structural rather
 * than cosmetic. That page is mounted as the `errorElement` of a TOP-LEVEL
 * route, so it renders in place of an element that contained `AppProviders` —
 * it has to bring its own translations and theme, and it fills the viewport
 * because there is nothing else left on screen.
 *
 * This one is mounted BELOW the shell: as the `errorElement` of a section
 * inside `AppLayout`'s `<Outlet/>`, or as the fallback of the boundary around
 * that outlet. Everything above it is still mounted and still working — the
 * providers, the sidebar, the header, the sockets, the chat client — so it
 * brings no providers of its own and claims only the content area. Reusing
 * `RouteErrorPage` in these positions would defeat the point: mounting a second
 * `TranslationsProvider`/`ThemeProvider` under the live ones, and painting over
 * a shell that never broke.
 */

export interface InlineRouteErrorViewProps {
  /**
   * Supplied by `RouteContentErrorBoundary`. A React error boundary latches:
   * once it has caught, it keeps rendering the fallback until something resets
   * it. The shell it preserves is exactly what invites the user to navigate
   * away, so without this a single broken page would turn every later route
   * into the same error. React Router clears its OWN route errors on
   * navigation, so the `errorElement` usage passes nothing.
   */
  onReset?: () => void;
}

export function InlineRouteErrorView({ onReset }: InlineRouteErrorViewProps) {
  const { formatMessage } = useIntl();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // The URL, not just the pathname: a page that picks its section from the
  // query (`/me/:id?section=subscriptions`) is left through a link that keeps
  // the pathname. Not `location.key` either — that changes on a navigation to
  // the very same URL, which would only re-render the same fault.
  const url = `${pathname}${search}`;
  const brokenUrl = React.useRef(url);

  React.useEffect(() => {
    if (url !== brokenUrl.current) onReset?.();
  }, [onReset, url]);

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      minHeight="60vh"
      height="100%"
      p={3}
    >
      <Stack alignItems="center" maxWidth={420} gap={2}>
        <Box
          // The mark is INK, not a fill, so it takes the measured on-surface
          // step rather than `error.main` (see the theme rules in
          // `packages/@mfw-frontend/CLAUDE.md`).
          sx={{ color: onSurface.error }}
          bgcolor="background.paper"
          justifyContent="center"
          alignItems="center"
          borderRadius="50%"
          display="flex"
          height={96}
          width={96}
          mb={1}
        >
          <Iconify
            icon="solar:danger-triangle-bold-duotone"
            size="large"
            width={48}
          />
        </Box>
        <Typography variant="h5">
          {formatMessage(messages.routeErrorTitle)}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {formatMessage(messages.routeErrorDescription)}
        </Typography>
        <Stack direction="row" gap={2} mt={2}>
          <Button
            onClick={() => window.location.reload()}
            data-testid="route-error-refresh"
            variant="contained"
          >
            {formatMessage(messages.routeErrorRefresh)}
          </Button>
          <Button
            // NAVIGATION, not an `href`. A full page load would discard the
            // shell this whole page exists to preserve — re-running the boot
            // and re-establishing every socket to reach a route the router can
            // show without leaving the page. The pathname change is also what
            // clears the boundary.
            onClick={() => navigate('/')}
            data-testid="route-error-back-home"
            variant="outlined"
            color="inherit"
          >
            {formatMessage(messages.backToHome)}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

/**
 * The `errorElement` for every section that renders inside the shell.
 *
 * It catches what a React boundary cannot see: a `lazy()` chunk that fails to
 * load (a deploy rolled over mid-session, the network dropped) and a loader
 * that threw — neither is a render error, so only the router knows about them.
 */
export function InlineRouteErrorPage() {
  const error = useRouteError();

  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
  }, [error]);

  return <InlineRouteErrorView />;
}

interface RouteContentErrorBoundaryProps {
  children: React.ReactNode;
}
interface RouteContentErrorBoundaryState {
  hasError: boolean;
}

/**
 * Wraps `AppLayout`'s `<Outlet/>`, and so covers RENDER faults on every route
 * under `/` — including the ones that have no `errorElement` of their own,
 * whose nearest router boundary is the route whose element IS the shell.
 */
export class RouteContentErrorBoundary extends React.Component<
  RouteContentErrorBoundaryProps,
  RouteContentErrorBoundaryState
> {
  state: RouteContentErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RouteContentErrorBoundaryState {
    return { hasError: true };
  }

  // The stack is the whole point of catching here rather than letting the
  // error reach `window.onerror`: it names WHICH page broke. Without it every
  // render fault in the app arrives as the same anonymous event whose only
  // frames are the shell. This is what `Sentry.withErrorBoundary` attaches, and
  // the reason the hand-rolled boundary has to attach it too.
  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return <InlineRouteErrorView onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
